import {
  Checkbox,
  Divider,
  FormControlLabel,
  FormHelperText,
  MenuItem,
} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import * as Sentry from '@sentry/browser';
import moment from 'moment';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ReactHtmlParser from 'react-html-parser';
import format from 'string-template';

import { ReactComponent as CalendarIcon } from '../../../../assets/icons/calendar-icon.svg';
import useComponentMounted from '../../../../hooks/useComponentMounted';
import useSessionStore from '../../../../hooks/useSessionStore';
import Program from '../../../../Model/Program';
import { DEFAULT_CURRENCY } from '../../../../utils/currency';
import { DateFormat, isFutureDay } from '../../../../utils/date';
import { getConfigPagePath } from '../../../../utils/routes';
import { formatCurrency } from '../../../../utils/formatters';
import { ConfigurationType, configurationPagesConfig } from '../../../config/configurationPages';
import MealPlanView from '../../../Model/MealPlanView';
import usePostHogFeatureFlag from '../../../../hooks/usePostHogFeatureFlag';
import { FeatureFlag, useChallengeCheckoutExperienceEnabled } from '../../../../utils/postHog';
import {
  CheckoutExperience,
  ContractMaxValue,
  ContractMinimumValue,
  ContractProperties,
  ContractType,
  DefaultContract,
  isCheckoutExperienceEligibleProduct,
  isStandardMonthlyCheckoutEligible,
  isDiscountedFirstMonthCheckoutEligible,
  getChallengeDurationDays,
  isContractPricingFree,
  isProgramLongEnoughForChallenge,
  getAllowedMaxInitialTerm,
} from '../../../utils/userContract';
import { DURATION_UNIT, formatPluralDuration, toMomentUnit } from '../../../../utils/durationUnit';
import RadioButtonGroup from '../../RadioButtonGroup';
import ChallengeConfigMealPlanSelector from '../ChallengeConfigMealPlanSelector';
import ChallengeProgramSelector from '../ChallengeProgramSelector';
import ContractModalContext from '../context/ContractModalContext';
import FeeInput from '../FeeInput';
import PackageCategoryCard from '../PackageCategoryCard';
import { isChallengeStillAccepting } from '../PackageCategoryCard/utils';
import PackagesSelector from '../PackagesSelector';
import PriceSelector, { CURRENCY_KEY } from '../PriceSelector';
import {
  Section,
  SectionTitle,
} from '../Section';
import ProductSelector from '../ProductSelector';
import { ContractPriceType } from '../utils/ContractTypes';

import {
  BillingOptionsContainer,
  BillingOptionsInfo,
  BillingOptionsPriceSelector,
  BillingOptionsRadioGroup,
  CancellationDate,
  ChallengeBadge,
  CheckoutPaymentWrapper,
  CommitmentContainer,
  ConfigRow,
  Container,
  ContractLabel,
  ContractLabelContainer,
  ContractValue,
  FormField,
  InitialPaymentContainer,
  InitialPaymentRowItem,
  InitialTermText,
  MonthlyPriceContainer,
  PackagesContainer,
  PostCommitmentContainer,
  PostCommitmentStartDate,
  ProductPackageContainer,
  ProgramCoverageError,
  SidePanel,
  sidePanelStyle,
  StartDateContainer,
  StyledAdornment,
  StyledTermUnitSelect,
  TermRowItem,
  StyledAveragePayment,
  StyledDatePickerInput,
  StyledInput,
  StyledPriceSelector,
  StyledProductSelector,
  SubSection,
  Toolbar,
  TotalFeesContainer,
  UpsellContainer,
  UpsellNote,
  UpsellSection,
  Wrapper,
  CardGrid,
  ChangePackageLink,
} from './styles';
import texts from './texts.json';

const priceTypeButtons = [
  {
    label: texts.pricing.standard,
    value: ContractPriceType.STANDARD,
  },
  {
    label: texts.pricing.custom,
    value: ContractPriceType.CUSTOM,
  },
];

// Ties the inline coverage error to the program selector via aria-describedby.
const PROGRAM_COVERAGE_ERROR_ID = 'challenge-program-coverage-error';

const BillingOption = {
  BILL_ON_START_DATE: 'BILL_ON_START_DATE',
  BILL_NOW: 'BILL_NOW',
};

const billingOptionsConfig = [
  {
    label: texts.billingOptions.optionLabels.billOnStartDate,
    value: BillingOption.BILL_ON_START_DATE,
  }, {
    label: texts.billingOptions.optionLabels.billNow,
    value: BillingOption.BILL_NOW,
  },
];

const checkoutExperienceOptions = [
  {
    label: texts.checkoutExperience.stripeHosted,
    value: CheckoutExperience.STRIPE_HOSTED,
  }, {
    label: texts.checkoutExperience.avidaManaged,
    value: CheckoutExperience.AVIDA_MANAGED,
  },
];

const ZERO_VALUE = '0';
const EMPTY_VALUE = '';

const ContractConfigurator = ({
  contract,
  contractDoc,
  onChange,
  onProductChange,
  selectedProduct,
  onPackageChange,
  supportedCurrencies,
  isReadOnly,
  showFeesConfigurator,
  showChallengeProducts,
  showPostCommitmentConfiguration,
  isAmendment,
}) => {
  const {
    resetContract,
    coachId,
    products,
    selectedPriceType: selectedContractPriceType,
    setSelectedPriceType: setSelectedContractPriceType,
    isPreviewTerms,
    isChallengeContract,
    selectedProgramId,
    setSelectedProgramId,
    setSelectedProgramWorkoutDays,
    selectedMealPlanId,
    setSelectedMealPlanId,
    acceptUntil,
    setAcceptUntil,
    categoryPackageEntries,
    categoryChallengeContract,
    isCategoryChallengeSelected,
    selectCategoryChallenge,
    showCategoryCards,
    blockedProductIds,
    contractType,
    lead,
    hasCardSelection,
    clearCardSelection,
    markCardPicked,
  } = useContext(ContractModalContext);

  const { isAdmin } = useSessionStore();

  const isComponentMountedRef = useComponentMounted();
  const [programs, setPrograms] = useState([]);
  const [mealPlans, setMealPlans] = useState([]);
  const [showAllCards, setShowAllCards] = useState(false);
  // Whether the coach has picked a card by hand, so the clearing below happens only on the way in.
  const hasPickedCardRef = useRef(false);

  const [selectedPriceType, setSelectedPriceType] = useState(() => {
    if (!contractDoc || isPreviewTerms) return selectedContractPriceType;

    return contractDoc.priceId ? ContractPriceType.STANDARD : ContractPriceType.CUSTOM;
  });

  const [billingOption, setBillingOption] = useState(() => (
    parseFloat(contract.upfrontPayment) > 0 ? BillingOption.BILL_NOW : BillingOption.BILL_ON_START_DATE
  ));

  /*
   * The selectedContractPriceType context value is used to determine the pricing mode of the contract.
   * If the local selection differs from the context value, it updates the context accordingly.
   */
  useEffect(() => {
    const setPriceMode = () => {
      if (selectedContractPriceType !== selectedPriceType) {
        setSelectedContractPriceType(selectedPriceType);
      }
    };

    setPriceMode();
  }, [
    selectedContractPriceType,
    selectedPriceType,
    setSelectedContractPriceType,
  ]);

  const onFieldChange = (event) => {
    const {
      target: {
        name,
        value,
      },
    } = event;

    if (value !== contract[name]) {
      onChange(name, value);
    }
  };

  const onPriceFieldChange = (event) => {
    const {
      target: {
        name,
        value,
      },
    } = event;
    let changedValue = value;

    // Allow empty value only for post commitment payment and upsell monthly price
    if ((name === ContractProperties.POST_COMMITMENT_PAYMENT || name === ContractProperties.UPSELL_MONTHLY_PRICE)
      && value === EMPTY_VALUE) {
      changedValue = EMPTY_VALUE;
    } else if (name !== CURRENCY_KEY) {
      /*
       * The price field change is triggered by the input change as well as the currency selection.
       * We only want to run through formatting if the change is the price input.
       */
      changedValue = Number(value.toString().match(/^\d+(?:\.\d{0,2})?/)).toString();
    }

    onChange(name, changedValue);
  };

  const minSubscriptionMonthsLowerLimit = Math.max(
    ContractMinimumValue.MIN_SUBSCRIPTION_MONTHS,
    contract.recurringBillingMonths,
  );

  /*
    The unit the contract term is counted in. Only challenge contracts can be
    sold in weeks — a regular subscription is billed by Stripe on a monthly
    interval, so its term is always months and the unit picker stays hidden.
  */
  const durationUnit = contract[ContractProperties.DURATION_UNIT] || DURATION_UNIT.MONTH;
  const isWeekTerm = durationUnit === DURATION_UNIT.WEEK;
  const termMomentUnit = toMomentUnit(durationUnit);
  /*
    Same ceiling the save gate applies, so the field can explain a value the
    gate would reject. A contract stored above the cap keeps its own value as
    the ceiling rather than being made unamendable.
  */
  const maxInitialTerm = getAllowedMaxInitialTerm(durationUnit, contractDoc?.initialTerm);
  const isTermOverMax = contract.initialTerm > maxInitialTerm;

  const termUnitOptions = useMemo(() => [
    { value: DURATION_UNIT.MONTH, label: contract.initialTerm === 1 ? texts.month : texts.months },
    { value: DURATION_UNIT.WEEK, label: contract.initialTerm === 1 ? texts.week : texts.weeks },
  ], [contract.initialTerm]);

  const commitmentErrorMessage = useMemo(() => {
    // Is it less than MIN_SUBSCRIPTION_MONTHS allowed?
    if (contract.minSubscriptionMonths < ContractMinimumValue.MIN_SUBSCRIPTION_MONTHS) {
      return format(texts.errors.minSubscriptionMonths, {
        0: ContractMinimumValue.MIN_SUBSCRIPTION_MONTHS,
        termUnit: isWeekTerm ? texts.weeks : texts.months,
      });
    }

    // If the commitment is not multiple of the billing cycle, then we should error out.
    if (contract.minSubscriptionMonths % contract.recurringBillingMonths !== 0) {
      const multiples = [1, 2, 3].map((item) => item * contract.recurringBillingMonths).join(', ');
      return format(texts.errors.minSubscriptionMonthsMultiple, { multiples });
    }

    // If the commitment is lower than the initial term, then we should error out.
    if (contract.minSubscriptionMonths < contract.initialTerm) {
      return format(texts.errors.minSubscriptionMonthsInitialTerm, contract.initialTerm);
    }

    return '';
  }, [contract, isWeekTerm]);

  // Compute the commitment end date based on the startDate and commitment.
  const commitmentEndDate = useMemo(() => {
    const startingDate = contract.startDate?.clone() || moment();

    return startingDate
      .add(contract.minSubscriptionMonths, termMomentUnit)
      .format(DateFormat.SHORT_MONTH_DAY_COMMA_YEAR_FORMAT);
  }, [
    contract.startDate,
    contract.minSubscriptionMonths,
    termMomentUnit,
  ]);

  const helperText = useMemo(() => {
    if (isAmendment) {
      return texts.amendmentHelperText;
    }
    if (contract.startDate?.isValid()) {
      return texts.startDateHelper;
    }
    return texts.errors.invalidDate;
  }, [isAmendment, contract.startDate]);

  const enableCustomPricing = selectedPriceType === ContractPriceType.CUSTOM;
  const selectedUpsellProduct = useMemo(
    () => products.find((p) => p.id === contract.upsellProductId) || null,
    [products, contract.upsellProductId],
  );

  const selectedProgram = useMemo(
    () => programs.find((program) => program.id === selectedProgramId) || null,
    [programs, selectedProgramId],
  );

  /*
    Surface the selected program's day-slot count to the provider so isValidContract can gate the create
    button. The provider only tracks selectedProgramId; programs (with their `.workouts`) are loaded here.
  */
  useEffect(() => {
    setSelectedProgramWorkoutDays(selectedProgram ? selectedProgram.workouts.length : null);
  }, [selectedProgram, setSelectedProgramWorkoutDays]);

  /*
    A challenge program must cover the full challenge period (initialTerm, counted in durationUnit). If it
    is too short, show an inline error; the same check disables contract creation via isValidContract in
    the provider.
  */
  const programCoverageError = useMemo(() => {
    if (!isChallengeContract || !selectedProgram || !contract.startDate?.isValid()) {
      return '';
    }
    const programWorkoutDays = selectedProgram.workouts.length;
    if (isProgramLongEnoughForChallenge(programWorkoutDays, contract.initialTerm, durationUnit, contract.startDate)) {
      return '';
    }
    return format(texts.errors.programTooShort, {
      programDays: programWorkoutDays,
      challengeDuration: formatPluralDuration(contract.initialTerm, durationUnit),
      challengeDays: getChallengeDurationDays(contract.initialTerm, durationUnit, contract.startDate),
    });
  }, [isChallengeContract, selectedProgram, contract.initialTerm, durationUnit, contract.startDate]);

  /*
   * A challenge is bought through the coach's own shared contract, at the price that contract
   * names, so nothing changed here is ever written — the configuration below describes the offering
   * rather than this sale. Editable fields would invite a discount that silently vanishes, so while
   * the challenge is the pick everything below the cards reads as settled. The cards themselves
   * stay live: picking a different offering is the whole reason to be here.
   */
  const isOfferingReadOnly = isReadOnly || isCategoryChallengeSelected;

  /**
   * We should disable the editing of the contract if:
   *  - The contract is read only (the client has already subscribed)
   *  - The coach is using standard pricing. In this case, the coach can select packages instead of individual prices.
   */
  const isEditingDisabled = isOfferingReadOnly || !enableCustomPricing;
  // Only show category cards when categories are actually configured; otherwise fall back to normal product selector.
  const effectiveShowCategoryCards = showCategoryCards
    && (categoryPackageEntries.length > 0 || !!categoryChallengeContract);
  // The card's includes/excludes come from the challenge product, the same source the package cards use.
  const categoryChallengeProduct = products.find(({ id }) => id === categoryChallengeContract?.productId) || null;

  const hasCollapsedSelection = (!!contract.packageId || isCategoryChallengeSelected) && !showAllCards;
  // While the cards show with nothing picked, there is no offering to configure yet.
  const showContractConfig = !effectiveShowCategoryCards || hasCardSelection;
  /*
   * These two describe how this lead's grid is being browsed, and the modal outlives the lead where
   * it is a panel rather than a dialog — the SDR inbox keeps one mounted while the rep clicks down
   * the list. Each lead therefore starts its own, as it would on a fresh open.
   */
  const leadId = lead?.id;
  useEffect(() => {
    hasPickedCardRef.current = false;
    setShowAllCards(false);
  }, [leadId]);

  /*
   * A challenge is seeded from a template that carries its own packageId, so that package would
   * otherwise keep showing as the selection. While the challenge is picked, no package is.
   */
  useEffect(() => {
    if (hasPickedCardRef.current || !isCategoryChallengeSelected) {
      return;
    }
    clearCardSelection();
    setShowAllCards(true);
  }, [isCategoryChallengeSelected, clearCardSelection]);

  const onCardPicked = () => {
    hasPickedCardRef.current = true;
    markCardPicked();
    setShowAllCards(false);
  };

  const getVisiblePackageEntries = () => {
    if (isCategoryChallengeSelected && !showAllCards) {
      return [];
    }
    if (hasCollapsedSelection) {
      return categoryPackageEntries.filter(({ pkg }) => pkg.id === contract.packageId);
    }
    // Packages belong to products, so the same rule applies here.
    return categoryPackageEntries.filter(({ product }) => !blockedProductIds.includes(product.id));
  };

  /*
   * The post commitment payment field should only be visible if:
   *  - The configurator is in an internal view (showPostCommitmentConfiguration is true), or
   *  - The contract document already includes a post commitment payment value
   *    (i.e. when coaches need to edit such a contract via the coach dashboard).
   */
  const showPostCommitmentRateOption = (contractDoc && !!contractDoc.postCommitmentPriceInCents)
    || showPostCommitmentConfiguration;

  /*
    If the initial term and minimum subscription months are the same
    then we should not allow the user to change the post commitment rate.
  */
  const isPostCommitmentEnabled = contract.minSubscriptionMonths !== contract.initialTerm;

  /*
    When the post commitment rate option is disabled, we reset its value to the default
  */
  useEffect(() => {
    if (!isPostCommitmentEnabled) {
      const property = ContractProperties.POST_COMMITMENT_PAYMENT;
      if (contract.postCommitmentPayment !== DefaultContract[property]) {
        onChange(property, DefaultContract[property]);
      }
    }
  }, [isPostCommitmentEnabled, contract, onChange]);

  useEffect(() => {
    const loadProgramsAndMealPlans = async () => {
      if (!coachId) return;

      try {
        const [coachPrograms, publicPrograms, mealPlanViews, publicMealPlanViews] = await Promise.all([
          Program.getProgramsByCoach(coachId),
          Program.getPublicPrograms(),
          MealPlanView.getMealPlanViewsByCoach(coachId),
          MealPlanView.getPublicMealPlanViews(),
        ]);

        if (isComponentMountedRef.current) {
          const allPrograms = [...coachPrograms.docs, ...publicPrograms.docs];
          setPrograms(allPrograms);
          const allMealPlans = MealPlanView.combineCoachAndPublic(mealPlanViews.docs, publicMealPlanViews.docs);
          setMealPlans(allMealPlans.filter((mp) => !mp.isArchived));
        }
      } catch (error) {
        Sentry.captureException(new Error('Error loading programs and meal plans'), {
          extra: {
            coachId,
            error,
          },
        });
      }
    };

    loadProgramsAndMealPlans();
  }, [coachId, isComponentMountedRef]);

  const isAiFriendContract = !!selectedProduct?.isAiFriendEnabled;

  const isStandardMonthlyHostedCheckoutEnabled = usePostHogFeatureFlag(
    FeatureFlag.STRIPE_HOSTED_CHECKOUT_STANDARD_MONTHLY,
  );
  const isDiscountedFirstMonthHostedCheckoutEnabled = usePostHogFeatureFlag(
    FeatureFlag.STRIPE_HOSTED_CHECKOUT_DISCOUNTED_FIRST_MONTH,
  );
  const isChallengeCheckoutExperienceEnabled = useChallengeCheckoutExperienceEnabled(coachId);

  /*
    Reusable contracts only. This configurator is also rendered for per-lead USER contracts
    (LeadContractModalContent, LeadModalContract) and for ONE_TIME ones, and S2-8138 leaves
    those on Avida-managed: without the type gate the selector appears on a lead contract,
    defaults it to Stripe Hosted, and the lead's ?leadId= onboarding then resolves a hosted
    type and sends them there. Gating the shared precondition rather than either branch keeps
    standard-monthly and discounted-first-month on one rule. Only ReusableContractModal opens
    the modal as BASE.
  */
  const canSelectCheckoutExperience = contractType === ContractType.BASE
    && isCheckoutExperienceEligibleProduct(selectedProduct)
    && Number(contract.monthlyPayment) > 0;
  const showStandardMonthlyCheckout = canSelectCheckoutExperience
    && isStandardMonthlyCheckoutEligible(contract)
    && isStandardMonthlyHostedCheckoutEnabled === true;
  const showDiscountedFirstMonthCheckout = canSelectCheckoutExperience
    && isDiscountedFirstMonthCheckoutEligible(contract)
    && isDiscountedFirstMonthHostedCheckoutEnabled === true;
  // Reusable challenge links only, for the same reason as canSelectCheckoutExperience.
  const isReusableChallengeContract = contractType === ContractType.BASE && isChallengeContract;
  const showChallengeCheckout = isReusableChallengeContract && isChallengeCheckoutExperienceEnabled;
  const showCheckoutExperience = showStandardMonthlyCheckout || showDiscountedFirstMonthCheckout
    || showChallengeCheckout;

  const checkoutExperience = contract[ContractProperties.CHECKOUT_EXPERIENCE];

  /*
    The coach's stored choice is cleared on the contract's SHAPE alone, deliberately not on the
    PostHog flags that showCheckoutExperience folds in. A flag is a rollout switch, not a statement
    about the contract: gated on the flags, turning the standard-monthly kill-switch off would blank
    checkoutExperience as soon as a coach opened an existing BASE contract set to Stripe Hosted, and
    the next unrelated save would persist Avida-managed — which turning the flag back on cannot
    undo. Defaulting still keys off showCheckoutExperience, because a value should only be written
    while the selector is actually on screen.
  */
  const isShapeEligible = isReusableChallengeContract || (canSelectCheckoutExperience
    && (isStandardMonthlyCheckoutEligible(contract) || isDiscountedFirstMonthCheckoutEligible(contract)));

  useEffect(() => {
    if (showCheckoutExperience && !checkoutExperience) {
      onChange(ContractProperties.CHECKOUT_EXPERIENCE, CheckoutExperience.STRIPE_HOSTED);
      return;
    }
    if (!isShapeEligible && checkoutExperience === CheckoutExperience.STRIPE_HOSTED) {
      onChange(ContractProperties.CHECKOUT_EXPERIENCE, '');
    }
  }, [showCheckoutExperience, isShapeEligible, checkoutExperience, onChange]);

  const totalFeeCollected = (() => {
    if (isChallengeContract) return contract[ContractProperties.CHALLENGE_FEE] || 0;
    if (isAiFriendContract) return contract[ContractProperties.AI_FRIEND_FEE] || 0;
    return (contract[ContractProperties.BASE_FEE] || 0)
      + (contract[ContractProperties.IS_FEE] || 0)
      + (contract[ContractProperties.AC_FEE] || 0);
  })();

  let upfrontPaymentErrorMessage = '';
  const upfrontPaymentError = parseFloat(contract.upfrontPayment) > parseFloat(contract.initialPayment);
  if (upfrontPaymentError) {
    const msg = format(texts.upfrontPaymentError, {
      amount: formatCurrency(contract.initialPayment, contract.currency),
    });
    upfrontPaymentErrorMessage = ReactHtmlParser(msg);
  }

  const onBillingOptionChange = (option) => {
    setBillingOption(option);
    if (option === BillingOption.BILL_ON_START_DATE) {
      /*
        We have to reset the upfront payment to 0 if the user selects to bill on start date, to avoid splitting the
        subscription in the backend
      */
      onFieldChange({ target: { name: ContractProperties.UPFRONT_PAYMENT, value: 0 } });
    }
  };

  const onPriceTypeChange = useCallback((option) => {
    if (option !== selectedPriceType) {
      setSelectedPriceType(option);
      resetContract();
    }
  }, [
    selectedPriceType,
    setSelectedPriceType,
    resetContract,
  ]);

  const onProgramChange = useCallback((program) => {
    setSelectedProgramId(program?.id || '');
    setSelectedProgramWorkoutDays(program ? program.workouts.length : null);
  }, [setSelectedProgramId, setSelectedProgramWorkoutDays]);

  return (
    <Container>
      <Toolbar>
        <RadioButtonGroup
          options={priceTypeButtons}
          selectedOption={selectedPriceType}
          onOptionChange={onPriceTypeChange}
        />
      </Toolbar>
      <SidePanel
        sx={sidePanelStyle}
      >
        <ProductPackageContainer>
          {effectiveShowCategoryCards ? (
            <Wrapper>
              <SectionTitle>{texts.selectPackage}</SectionTitle>
              <CardGrid>
                {getVisiblePackageEntries().map(({ pkg, product }) => (
                  <PackageCategoryCard
                    key={pkg.id}
                    pkg={pkg}
                    product={product}
                    isSelected={!isCategoryChallengeSelected && pkg.id === contract.packageId}
                    isReadOnly={isReadOnly}
                    onClick={() => {
                      if (!isReadOnly) {
                        onCardPicked();
                        onProductChange(product);
                        // The product state has not updated yet, so the package names its own.
                        onPackageChange(pkg, product);
                      }
                    }}
                  />
                ))}
                {!!categoryChallengeContract && (!hasCollapsedSelection || isCategoryChallengeSelected) && (
                  <PackageCategoryCard
                    contract={categoryChallengeContract}
                    product={categoryChallengeProduct}
                    isSelected={isCategoryChallengeSelected && hasCardSelection}
                    isReadOnly={isReadOnly}
                    // Past its accept-until date the challenge can no longer be sold, so it shows but cannot be picked.
                    isDisabled={!isChallengeStillAccepting(categoryChallengeContract)}
                    onClick={() => {
                      if (!isReadOnly) {
                        onCardPicked();
                        selectCategoryChallenge(categoryChallengeContract);
                      }
                    }}
                  />
                )}
              </CardGrid>
              {hasCollapsedSelection && !isReadOnly && (
                <ChangePackageLink onClick={() => setShowAllCards(true)}>
                  {texts.changePackage}
                </ChangePackageLink>
              )}
            </Wrapper>
          ) : (
            <>
              <SubSection>
                <SectionTitle>
                  {texts.selectProduct}
                  {isChallengeContract && (
                    <ChallengeBadge>{texts.challengeContractLabel}</ChallengeBadge>
                  )}
                </SectionTitle>
                <StyledProductSelector
                  selectedProduct={selectedProduct}
                  onChange={onProductChange}
                  isReadOnly={isOfferingReadOnly || !coachId}
                  products={products.filter((product) => (
                    (!product.isArchived || product.id === selectedProduct?.id)
                    && (showChallengeProducts || !product.isChallengeProduct)
                    && (!blockedProductIds.includes(product.id) || product.id === selectedProduct?.id)
                  ))}
                />
              </SubSection>
              {!!selectedProduct && !enableCustomPricing && (
                <SubSection>
                  <SectionTitle>{texts.selectPackage}</SectionTitle>
                  <PackagesContainer>
                    <PackagesSelector
                      product={selectedProduct}
                      selectedPackageId={contract.packageId}
                      onChange={onPackageChange}
                      isReadOnly={isOfferingReadOnly}
                    />
                  </PackagesContainer>
                </SubSection>
              )}
            </>
          )}
        </ProductPackageContainer>
        {showContractConfig && isChallengeContract && (
          <Section>
            <SectionTitle>{texts.challengeConfiguration}</SectionTitle>
            <ConfigRow>
              <FormField>
                <ChallengeProgramSelector
                  programs={programs}
                  selectedProgramId={selectedProgramId}
                  onChange={onProgramChange}
                  isReadOnly={isOfferingReadOnly}
                  hasError={!!programCoverageError}
                  errorId={PROGRAM_COVERAGE_ERROR_ID}
                />
                {!!programCoverageError && (
                  <ProgramCoverageError
                    id={PROGRAM_COVERAGE_ERROR_ID}
                    role="alert"
                  >
                    {programCoverageError}
                  </ProgramCoverageError>
                )}
              </FormField>
              <FormField>
                <ChallengeConfigMealPlanSelector
                  mealPlans={mealPlans}
                  selectedMealPlanId={selectedMealPlanId}
                  onChange={(mealPlan) => setSelectedMealPlanId(mealPlan?.id || '')}
                  isReadOnly={isOfferingReadOnly}
                />
              </FormField>
              <FormField>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DesktopDatePicker
                    label={texts.acceptUntilLabel}
                    value={acceptUntil}
                    disablePast
                    onChange={(newValue) => setAcceptUntil(newValue)}
                    disabled={isOfferingReadOnly}
                    components={{
                      OpenPickerIcon: CalendarIcon,
                    }}
                    renderInput={(params) => (
                      <StyledDatePickerInput
                        size="small"
                        {...params}
                      />
                    )}
                  />
                </LocalizationProvider>
              </FormField>
            </ConfigRow>
          </Section>
        )}
        {showContractConfig && (
          <Section>
            <SectionTitle>{texts.dueOnCheckout}</SectionTitle>
            <ConfigRow>
              <CheckoutPaymentWrapper>
                <InitialPaymentContainer $hasUnitPicker={isChallengeContract}>
                  <InitialPaymentRowItem>
                    <PriceSelector
                      name={ContractProperties.INITIAL_PAYMENT}
                      contract={contract}
                      currency={contract.currency}
                      value={contract.initialPayment}
                      fieldText={texts.initialPayment}
                      isReadOnly={isEditingDisabled}
                      onChange={onPriceFieldChange}
                      supportedCurrencies={supportedCurrencies}
                    />
                  </InitialPaymentRowItem>
                  <InitialTermText>{texts.for}</InitialTermText>
                  <TermRowItem $hasUnitPicker={isChallengeContract}>
                    <FormField>
                      <StyledInput
                        $hasUnitPicker={isChallengeContract}
                        $wideNumber={isChallengeContract}
                        size="small"
                        name={ContractProperties.INITIAL_TERM}
                        label={texts.initialTerm}
                        value={contract.initialTerm}
                        onChange={onFieldChange}
                        type="number"
                        disabled={isEditingDisabled}
                        error={contract.initialTerm <= 0 || isTermOverMax}
                        helperText={(() => {
                          if (contract.initialTerm <= 0) return texts.errors.positiveNumber;
                          // Without this the save button just goes dead with nothing pointing at the field.
                          if (isTermOverMax) {
                            return format(texts.errors.initialTermMax, {
                              max: maxInitialTerm,
                              termUnit: isWeekTerm ? texts.weeks : texts.months,
                            });
                          }
                          return '';
                        })()}
                        InputProps={{
                          inputProps: {
                            min: ContractMinimumValue.INITIAL_TERM,
                            max: maxInitialTerm,
                          },
                          /*
                            Challenges can be sold by the week, so their term
                            unit is a picker rather than a fixed label. Every
                            other contract keeps the static "month(s)" adornment.
                          */
                          endAdornment: isChallengeContract ? (
                            <StyledTermUnitSelect
                              size="small"
                              value={durationUnit}
                              onChange={(e) => onChange(ContractProperties.DURATION_UNIT, e.target.value)}
                              /*
                                Same gate as the term and price fields beside it.
                                Under standard pricing those lock to the
                                package's preset values, so leaving the unit
                                editable would let a coach reinterpret a fixed
                                term — turning a package-priced 6-month
                                challenge into 6 weeks at the same price, with
                                no way to correct the count.
                              */
                              disabled={isEditingDisabled}
                              inputProps={{ 'aria-label': texts.durationUnit }}
                            >
                              {termUnitOptions.map(({ value, label }) => (
                                <MenuItem key={value} value={value}>{label}</MenuItem>
                              ))}
                            </StyledTermUnitSelect>
                          ) : (
                            <StyledAdornment position="end">
                              {contract.initialTerm === 1 ? texts.month : texts.months}
                            </StyledAdornment>
                          ),
                        }}
                        id="initial-term"
                        fullWidth
                      />
                    </FormField>
                  </TermRowItem>
                </InitialPaymentContainer>
                <StyledAveragePayment contract={contract} />
              </CheckoutPaymentWrapper>
              <Divider orientation="vertical" flexItem />
              <CommitmentContainer>
                <FormField>
                  <StyledInput
                    $wideNumber={isChallengeContract}
                    size="small"
                    name={ContractProperties.MIN_SUBSCRIPTION_MONTHS}
                    label={texts.commitment}
                    value={contract.minSubscriptionMonths}
                    onChange={onFieldChange}
                    type="number"
                    disabled={isOfferingReadOnly || !!contract.cancelAtPeriodEnd || isChallengeContract}
                    error={!!commitmentErrorMessage}
                    helperText={commitmentErrorMessage}
                    InputProps={{
                      inputProps: {
                        min: minSubscriptionMonthsLowerLimit,
                        placeholder: contract.minSubscriptionMonthsPlaceholder,
                      },
                      endAdornment: (
                        <StyledAdornment position="end">
                          {(() => {
                            if (isWeekTerm) {
                              return contract.minSubscriptionMonths === 1 ? texts.week : texts.weeks;
                            }
                            return contract.minSubscriptionMonths === 1 ? texts.month : texts.months;
                          })()}
                        </StyledAdornment>
                      ),
                    }}
                    fullWidth
                    id="commitment"
                  />
                </FormField>
              </CommitmentContainer>
              <CancellationDate>
                <span>{ReactHtmlParser(format(texts.cancelDate, { date: commitmentEndDate }))}</span>
              </CancellationDate>
            </ConfigRow>
          </Section>
        )}
        {showContractConfig && !isChallengeContract && (
          <Section>
            <SectionTitle>{texts.monthlySubscription}</SectionTitle>
            <MonthlyPriceContainer>
              <StyledPriceSelector
                name={ContractProperties.MONTHLY_PAYMENT}
                currency={contract.currency}
                value={contract.monthlyPayment}
                fieldText={texts.monthlyPayment}
                /*
                  If the initial term is 1 (meaning a monthly subscription), then we should not allow the user to change
                  the monthly payment. This is because the monthly payment is the same as the initial payment.
                  For longer initialTerms, the monthly payment will be different from the initial payment, so we enable
                  the field.
                */
                isReadOnly={isEditingDisabled}
                onChange={onPriceFieldChange}
                supportedCurrencies={supportedCurrencies}
              />
              {showPostCommitmentRateOption && (
                <PostCommitmentContainer>
                  <StyledPriceSelector
                    name={ContractProperties.POST_COMMITMENT_PAYMENT}
                    currency={contract.currency}
                    value={contract.postCommitmentPayment}
                    fieldText={texts.postCommitmentPayment}
                    isReadOnly={isOfferingReadOnly || !isPostCommitmentEnabled}
                    onChange={onPriceFieldChange}
                    supportedCurrencies={supportedCurrencies}
                    errorMessage={contract.postCommitmentPayment === ZERO_VALUE ? texts.errors.zeroError : EMPTY_VALUE}
                  />
                  {contract.postCommitmentPayment !== EMPTY_VALUE && (
                    <PostCommitmentStartDate>
                      <span>{ReactHtmlParser(format(texts.postCommitmentStartDate, { date: commitmentEndDate }))}</span>
                    </PostCommitmentStartDate>
                  )}
                </PostCommitmentContainer>
              )}
            </MonthlyPriceContainer>
          </Section>
        )}
        {/*
          Admin-only skip-terms toggle for comped links (S2-8767). Checked
          state and persistence are both tied to a fully $0 contract, so
          raising any price disarms the flag without extra bookkeeping.
        */}
        {showContractConfig && contractType === ContractType.ONE_TIME && isAdmin && (
          <Section>
            <SectionTitle>{texts.freeContractOptions}</SectionTitle>
            <FormControlLabel
              control={(
                <Checkbox
                  checked={!!contract.skipTerms && isContractPricingFree(contract)}
                  onChange={(event) => onChange(ContractProperties.SKIP_TERMS, event.target.checked)}
                  disabled={isOfferingReadOnly || !isContractPricingFree(contract)}
                />
              )}
              label={texts.skipTermsLabel}
            />
            <FormHelperText>{texts.skipTermsHelper}</FormHelperText>
          </Section>
        )}
        {showContractConfig && isChallengeContract && (
          <UpsellSection>
            <SectionTitle>{texts.upsellConfiguration}</SectionTitle>
            {/* Legacy per-contract upsell: kept read-only where already configured (old app
                versions still read it); new contracts configure upsells in Upsell Settings. */}
            {contract.upsellProductId ? (
              <UpsellContainer>
                <FormField>
                  <ProductSelector
                    selectedProduct={selectedUpsellProduct}
                    onChange={() => {}}
                    isReadOnly
                    // keep archived selected product visible while excluding challenge products for upsell offers
                    products={products.filter((product) => (
                      !product.isChallengeProduct
                      && (!product.isArchived || product.id === selectedUpsellProduct?.id)
                    ))}
                    selectId="upsell-product-selector"
                    label={texts.upsellProduct}
                  />
                </FormField>
                <StyledPriceSelector
                  name={ContractProperties.UPSELL_MONTHLY_PRICE}
                  currency={contract.currency || DEFAULT_CURRENCY.code}
                  value={contract.upsellMonthlyPrice ?? ''}
                  fieldText={texts.upsellMonthlyPrice}
                  isReadOnly
                  isCurrencyReadOnly
                  onChange={onPriceFieldChange}
                  supportedCurrencies={supportedCurrencies}
                  errorMessage={EMPTY_VALUE}
                />
              </UpsellContainer>
            ) : null}
            <UpsellNote>
              {texts.upsellManagedInSettings}
              {!!coachId && (
                <Link
                  to={getConfigPagePath(coachId, configurationPagesConfig[ConfigurationType.UPSELL_SETTINGS].path)}
                  target="_blank"
                >
                  {texts.upsellSettingsLink}
                </Link>
              )}
            </UpsellNote>
          </UpsellSection>
        )}
        {/* The sticker flow reads the flag off the challenge's BASE contract only, so a one-off's copy would lie. */}
        {showContractConfig && isReusableChallengeContract && (
          <Section>
            <SectionTitle>{texts.storySharing}</SectionTitle>
            <FormControlLabel
              control={(
                <Checkbox
                  checked={!!contract[ContractProperties.IS_STORY_SHARE_ENABLED]}
                  onChange={(event) => onChange(ContractProperties.IS_STORY_SHARE_ENABLED, event.target.checked)}
                  disabled={isOfferingReadOnly}
                />
              )}
              label={texts.storySharingLabel}
            />
            <FormHelperText>{texts.storySharingHelper}</FormHelperText>
          </Section>
        )}
        {showContractConfig && (
          <Section>
            <SectionTitle>{texts.startDate}</SectionTitle>
            <StartDateContainer>
              <FormField>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DesktopDatePicker
                    disablePast
                    name={ContractProperties.START_DATE}
                    inputFormat={DateFormat.DATE_FORMAT_SLASH}
                    value={contract.startDate}
                    disabled={isOfferingReadOnly || isAmendment}
                    onChange={(dt) => onFieldChange({ target: { name: 'startDate', value: dt } })}
                    components={{
                      OpenPickerIcon: CalendarIcon,
                    }}
                    renderInput={(params) => (
                      <StyledDatePickerInput
                        size="small"
                        {...params}
                        error={!isOfferingReadOnly && !contract.startDate?.isValid()}
                        helperText={helperText}
                      />
                    )}
                  />
                </LocalizationProvider>
              </FormField>
            </StartDateContainer>
          </Section>
        )}
        {/*
          A challenge is billed in full at checkout, so there is no advance to configure — the
          section is left out rather than shown with nothing to choose. `isFutureDay` is the one
          definition of a future start date the form uses, so this cannot disagree with the handler
          that clears the advance.
        */}
        {showContractConfig
        && contract.startDate?.isValid()
        && isFutureDay(contract.startDate)
        && !isChallengeContract
        && !isAmendment
        && (
          <Section>
            <SectionTitle>{texts.billingOptions.title}</SectionTitle>
            <BillingOptionsContainer>
              <BillingOptionsRadioGroup>
                <RadioButtonGroup
                  options={billingOptionsConfig}
                  selectedOption={billingOption}
                  onOptionChange={onBillingOptionChange}
                />
              </BillingOptionsRadioGroup>
              <BillingOptionsPriceSelector
                name={ContractProperties.UPFRONT_PAYMENT}
                currency={contract.currency}
                value={contract.upfrontPayment}
                fieldText={texts.upfrontPayment}
                isReadOnly={billingOption === BillingOption.BILL_ON_START_DATE || isOfferingReadOnly}
                onChange={onPriceFieldChange}
                supportedCurrencies={supportedCurrencies}
                errorMessage={upfrontPaymentErrorMessage}
                isCurrencyReadOnly
              />
              <BillingOptionsInfo>
                <ContractLabelContainer>
                  <ContractLabel>{`${texts.billingOptions.infoLabels.billedImmediately}: `}</ContractLabel>
                  <ContractValue>{formatCurrency(contract.upfrontPayment, contract.currency)}</ContractValue>
                </ContractLabelContainer>
                <ContractLabelContainer>
                  <ContractLabel>{`${texts.billingOptions.infoLabels.billedOnStartDate}: `}</ContractLabel>
                  <ContractValue>
                    {formatCurrency(contract.initialPayment - contract.upfrontPayment, contract.currency)}
                    {` (${contract.startDate.format(DateFormat.DATE_FORMAT_COMMA)})`}
                  </ContractValue>
                </ContractLabelContainer>
              </BillingOptionsInfo>
            </BillingOptionsContainer>
          </Section>
        )}
        {showContractConfig && showCheckoutExperience && (
          <Section>
            <SectionTitle>{texts.checkoutExperience.title}</SectionTitle>
            <RadioButtonGroup
              options={checkoutExperienceOptions}
              selectedOption={checkoutExperience || CheckoutExperience.STRIPE_HOSTED}
              onOptionChange={(value) => onChange(ContractProperties.CHECKOUT_EXPERIENCE, value)}
              disabled={isOfferingReadOnly}
            />
          </Section>
        )}
        {showContractConfig && showFeesConfigurator && (
          <Section>
            <SectionTitle>{texts.collectedFees}</SectionTitle>
            <ConfigRow>
              {typeof contract[ContractProperties.AI_FRIEND_FEE] !== 'undefined' && (
                <FeeInput
                  label={format(texts.override, { feeType: texts.aiFriendFee })}
                  value={contract[ContractProperties.AI_FRIEND_FEE]}
                  name={ContractProperties.AI_FRIEND_FEE}
                  onChange={onFieldChange}
                  max={ContractMaxValue.AI_FRIEND_FEE_PERCENTAGE}
                />
              )}
              {typeof contract[ContractProperties.CHALLENGE_FEE] !== 'undefined' && (
                <FeeInput
                  label={format(texts.override, { feeType: texts.challengeFee })}
                  value={contract[ContractProperties.CHALLENGE_FEE]}
                  name={ContractProperties.CHALLENGE_FEE}
                  onChange={onFieldChange}
                  max={ContractMaxValue.CHALLENGE_FEE_PERCENTAGE}
                />
              )}
              {typeof contract[ContractProperties.BASE_FEE] !== 'undefined' && (
                <FeeInput
                  label={format(texts.override, { feeType: texts.baseFee })}
                  value={contract[ContractProperties.BASE_FEE]}
                  name={ContractProperties.BASE_FEE}
                  onChange={onFieldChange}
                />
              )}
              {typeof contract[ContractProperties.IS_FEE] !== 'undefined' && (
                <FeeInput
                  label={format(texts.override, { feeType: texts.isFee })}
                  value={contract[ContractProperties.IS_FEE]}
                  name={ContractProperties.IS_FEE}
                  onChange={onFieldChange}
                />
              )}
              {typeof contract[ContractProperties.AC_FEE] !== 'undefined' && (
                <FeeInput
                  label={format(texts.override, { feeType: texts.acFee })}
                  value={contract[ContractProperties.AC_FEE]}
                  name={ContractProperties.AC_FEE}
                  onChange={onFieldChange}
                />
              )}
            </ConfigRow>
            <TotalFeesContainer>
              {ReactHtmlParser(format(texts.totalFeeCollected, { totalFeeCollected }))}
            </TotalFeesContainer>
          </Section>
        )}
      </SidePanel>
    </Container>
  );
};

ContractConfigurator.propTypes = {
  contract: PropTypes.object.isRequired,
  contractDoc: PropTypes.object,
  onProductChange: PropTypes.func,
  selectedProduct: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  isReadOnly: PropTypes.bool,
  onPackageChange: PropTypes.func.isRequired,
  supportedCurrencies: PropTypes.arrayOf(PropTypes.string),
  showFeesConfigurator: PropTypes.bool,
  showChallengeProducts: PropTypes.bool,
  showPostCommitmentConfiguration: PropTypes.bool,
  isAmendment: PropTypes.bool.isRequired,
};

ContractConfigurator.defaultProps = {
  contractDoc: null,
  isReadOnly: false,
  onProductChange: () => { },
  selectedProduct: null,
  supportedCurrencies: [DEFAULT_CURRENCY.code],
  showFeesConfigurator: false,
  showChallengeProducts: false,
  showPostCommitmentConfiguration: false,
};

export default ContractConfigurator;
